// traceAnatomy.js — the per-trace data behind the trace-detail anatomy hero.
//
// Two pure builders, one per band:
//
//   • buildTraceProvenance — the provenance graph INSIDE one trace's context
//     window. The window-scan pipeline (extractors → collectWindow* →
//     traceProvenance) already computes this graph across a scan window of
//     prompts; here the "window" is a single trace whose turns hold the whole
//     session history, which is where the same engine has its densest data
//     (a scan window is a minutes-wide cross-section; a context window is the
//     full session so far).
//
//   • buildJourney — the session's path through cluster space, folded from the
//     per-prompt cluster-assignment sequence into runs and revisit edges.
//
// Both are DOM-free and deterministic so the geometry can be unit-tested; the
// component maps their output to pixels (same split as termArch/lineageLayouts).

import { extractFileOps, collectWindowOps } from "./traceFileTree.js";
import { extractLinearOps, collectWindowLinearOps } from "./traceLinearOps.js";
import { extractWebResearchOps, collectWindowWebResearchOps } from "./traceWebResearch.js";
import { extractForgeOps, collectWindowForgeOps } from "./traceForgeOps.js";
import { extractGrafanaOps, extractDataQueryOps, collectWindowQueryOps } from "./traceQueryOps.js";
import { extractRecallOps, collectWindowRecallOps } from "./traceRecallOps.js";
import {
  fileNodes, linearNodes, webResearchNodes, forgeNodes, queryNodes, recallNodes,
  buildProvenance,
} from "./traceProvenance.js";
import { orderedNodes } from "./lineageLayouts.js";

/**
 * tool_use block id → position in the conversation. Within one trace every op
 * shares the prompt's timestamp, and provenance's source-earlier→target-later
 * mechanism (plus every layout's node order) needs authored order — so ops get
 * distinct pseudo-timestamps from their block position instead.
 */
export function opSequenceMap(turns) {
  const map = new Map();
  let seq = 0;
  for (const turn of turns || []) {
    for (const block of turn?.blocks || []) {
      seq += 1;
      if (block?.type === "tool_use" && block.id && !map.has(block.id)) map.set(block.id, seq);
    }
  }
  return map;
}

// The most ops the per-trace provenance pass will consider (newest kept).
export const PROVENANCE_OP_CAP = 300;

// A semantic-feed domain -> its family's window flattener (session stamping,
// first sighting per key, forge's repo adoption) and its node adapter. Keyed by
// the FEED's domain names, so an op reaches provenance through the same table
// whichever door produced it: an extractor run here, or a lane item from the
// shell grammar or a code-mode cell.
const DOMAIN_PROVENANCE = {
  files: [collectWindowOps, fileNodes],
  linear: [collectWindowLinearOps, linearNodes],
  research: [collectWindowWebResearchOps, webResearchNodes],
  forge: [collectWindowForgeOps, forgeNodes],
  grafana: [collectWindowQueryOps, queryNodes],
  query: [collectWindowQueryOps, queryNodes],
  recall: [collectWindowRecallOps, recallNodes],
};

/**
 * The provenance graph over ops already grouped by feed domain.
 *
 * @param {Array<[string, Array]>} opsByDomain  [domain, ops] pairs, in the
 *   order ties should break
 * @param {{sessionId, promptId, endpointId, ts}} meta  the trace's identity
 * @param {(key: string) => number | undefined} seqOf  an op's authored position
 * @returns {{nodes, edges, droppedOps}}  buildProvenance output
 */
export function provenanceOverOps(opsByDomain, meta, seqOf) {
  const base = meta?.ts instanceof Date ? meta.ts.getTime() : Number(meta?.ts) || 0;
  const promptRow = (ops) => [{
    promptId: meta?.promptId || "",
    sessionId: meta?.sessionId || "",
    endpointId: meta?.endpointId || "",
    endpointName: "",
    ts: base,
    ops,
  }];
  const nodes = [];
  for (const [domain, ops] of opsByDomain) {
    const family = DOMAIN_PROVENANCE[domain];
    if (!family || !ops?.length) continue;
    const [collect, adapt] = family;
    nodes.push(...adapt(collect(promptRow(ops)).map((op) => ({ ...op, ts: base + (seqOf(op.key) ?? 0) * 1000 }))));
  }
  // buildProvenance is pairwise (documented for a minutes-wide scan window);
  // a long agentic session accumulates enough ops in one context to make the
  // quadratic pass expensive on the main thread, re-run per trace click.
  // Keep the NEWEST ops — they carry the story the panel opens on — and
  // surface the drop (droppedOps) so a cap is never silent.
  nodes.sort((a, b) => (a.ts || 0) - (b.ts || 0));
  const dropped = Math.max(0, nodes.length - PROVENANCE_OP_CAP);
  const built = buildProvenance(dropped ? nodes.slice(dropped) : nodes);
  built.droppedOps = dropped;
  return built;
}

/**
 * The provenance graph for ONE trace: run every extractor over its turns,
 * stamp ops with sequence-ordered pseudo-timestamps, adapt to provenance
 * nodes, and build edges. Same assembly as XRayBody's lineageNodes, minus the
 * factory lenses (registry access is a host concern, and the committed
 * extractors carry the graph — see the measured counts on the design repro).
 *
 * @param {Array} turns  parsed turns (parseApiRequest shape)
 * @param {{sessionId, promptId, endpointId, ts}} meta  the trace's identity
 * @returns {{nodes, edges}}  buildProvenance output
 */
export function buildTraceProvenance(turns, meta) {
  const seq = opSequenceMap(turns);
  // One throwing extractor must not blank the panel — same boundary rule as
  // the X-ray scan pass.
  const ex = (fn) => {
    try { return fn(turns) || []; }
    catch (err) { console.warn("[trace-anatomy] extractor threw", err); return []; }
  };
  return provenanceOverOps([
    ["files", ex(extractFileOps)],
    ["linear", ex(extractLinearOps)],
    ["research", ex(extractWebResearchOps)],
    ["forge", ex(extractForgeOps)],
    ["grafana", ex(extractGrafanaOps)],
    ["query", ex(extractDataQueryOps)],
    ["recall", ex(extractRecallOps)],
  ], meta, (key) => seq.get(key));
}

/** Family of a node kind, mirroring lineageLayouts.familyOf. */
export const familyOf = (kind) => String(kind || "").split(".")[0] || "other";

// Categorical hues for cluster identity — mid-lightness so they hold up on
// both themes. Shared by the journey band and the metadata cluster bar, both
// of which assign hues in volume-descending order over the same session, so
// the two surfaces mostly agree on a cluster's color.
export const CLUSTER_HUES = ["#8fb376", "#7fa3c9", "#c9a45f", "#a98fc9", "#c98383", "#7bbfae", "#bdb56e", "#8b9dc9"];

/** Compact display label: file basename, web host, else the node's label. */
export function shortNodeLabel(n) {
  if (!n) return "";
  const kind = String(n.kind || "");
  if (kind.startsWith("file") && n.ref) {
    const segs = String(n.ref).replace(/\\/g, "/").split("/").filter(Boolean);
    return segs[segs.length - 1] || String(n.ref);
  }
  if (kind.startsWith("web") && n.ref) {
    try { return new URL(n.ref).host; } catch { return String(n.ref).slice(0, 30); }
  }
  return String(n.label || n.ref || kind).slice(0, 40);
}

// Evidence-tier order, strongest first — used to keep the strongest claim
// when parallel edges between two artifacts collapse into one.
const TIER_RANK = { cited: 0, quoted: 1, entity: 2, adjacent: 3 };

/**
 * Index-space projection of the provenance graph for the arc timeline,
 * COARSE-GRAINED: consecutive ops on the same artifact collapse into one
 * node (count = ops), and parallel edges between two collapsed nodes merge
 * into one carrying the strongest tier and a count. Twenty back-to-back
 * queries against one table are one fact, not twenty arcs — drawing them
 * all is what turned the consultation half into a wall.
 */
export function arcModel(built) {
  const ops = orderedNodes(built);
  const identityOf = (n) => `${familyOf(n.kind)}:${n.ref || n.id}`;
  const nodes = [];
  const nodeIndexOfOp = new Map(); // op id → collapsed node index
  for (const op of ops) {
    const identity = identityOf(op);
    const last = nodes[nodes.length - 1];
    if (last && last.identity === identity) {
      last.count += 1;
      nodeIndexOfOp.set(op.id, nodes.length - 1);
      continue;
    }
    nodes.push({
      id: op.id, identity, kind: op.kind, family: familyOf(op.kind),
      label: shortNodeLabel(op), count: 1, degree: 0,
    });
    nodeIndexOfOp.set(op.id, nodes.length - 1);
  }
  const merged = new Map(); // "s→t|class" → edge
  for (const e of built?.edges || []) {
    const s = nodeIndexOfOp.get(e.source.id);
    const t = nodeIndexOfOp.get(e.target.id);
    if (s == null || t == null || s === t) continue;
    const key = `${s}→${t}|${e.class}`;
    const cur = merged.get(key);
    if (cur) {
      cur.count += 1;
      if ((TIER_RANK[e.tier] ?? 9) < (TIER_RANK[cur.tier] ?? 9)) cur.tier = e.tier;
      continue;
    }
    merged.set(key, {
      s, t,
      tier: e.tier,
      consultation: e.class === "consultation",
      count: 1,
      evidence: Array.isArray(e.evidence) ? e.evidence[0] : null,
    });
  }
  const edges = [...merged.values()];
  for (const e of edges) {
    nodes[e.s].degree += 1;
    nodes[e.t].degree += 1;
  }
  // Tall spans first so short arcs draw over them, same as arcLayout.
  edges.sort((a, b) => Math.abs(b.s - b.t) - Math.abs(a.s - a.t));
  return { nodes, edges, opCount: ops.length, droppedOps: built?.droppedOps || 0 };
}

/**
 * Majority-bin a slice of the cluster sequence into nBins buckets — calm
 * blocks instead of per-prompt confetti at the journey band's resolution.
 * Each bin carries the majority cluster of its index span (first-seen wins a
 * tie). [i0, i1] is the visible index range, both ends inclusive; a zoomed
 * band re-bins over fewer prompts and discloses detail progressively. When
 * bins outnumber prompts, each bin clamps to at least one index so the strip
 * stays contiguous instead of gapping at deep zoom.
 *
 * @returns {Array<{b: number, clusterId}>}
 */
export function journeyBins(rows, i0, i1, nBins) {
  const bins = [];
  const span = i1 - i0 + 1;
  for (let b = 0; b < nBins; b++) {
    const from = i0 + Math.floor((span * b) / nBins);
    const to = Math.max(from, i0 + Math.floor((span * (b + 1)) / nBins) - 1);
    const counts = new Map();
    for (let i = from; i <= to && i < rows.length; i++) {
      const id = rows[i].clusterId;
      counts.set(id, (counts.get(id) || 0) + 1);
    }
    let major = null;
    let best = 0;
    for (const [id, c] of counts) if (c > best) { best = c; major = id; }
    if (major == null) continue;
    bins.push({ b, clusterId: major });
  }
  return bins;
}

/**
 * Fold a session's per-prompt cluster sequence into the journey band's model:
 * consecutive same-cluster prompts merge into runs; a run re-entering a
 * cluster seen earlier yields a revisit edge (the arcs); the focal trace is
 * located by event id, else by nearest timestamp.
 *
 * @param {Array<{eventId, tsMs, clusterId, clusterName}>} rows  time-ascending
 * @returns {null | {rows, runs, revisits, topClusters, clusterCount, focalIdx}}
 */
export function buildJourney(rows, { focalEventId = null, focalTsMs = null } = {}) {
  const seq = (rows || []).filter((r) => r && r.clusterId != null);
  if (!seq.length) return null;
  const volume = new Map();
  for (const r of seq) volume.set(r.clusterId, (volume.get(r.clusterId) || 0) + 1);
  const topClusters = [...volume.entries()]
    .sort((a, b) => (b[1] - a[1]) || String(a[0]).localeCompare(String(b[0])))
    .slice(0, 8)
    .map(([id, count]) => ({
      id, count,
      name: seq.find((r) => r.clusterId === id)?.clusterName || `topic ${id}`,
    }));
  const runs = [];
  seq.forEach((r, i) => {
    const last = runs[runs.length - 1];
    if (last && last.clusterId === r.clusterId) { last.end = i; return; }
    runs.push({ clusterId: r.clusterId, start: i, end: i });
  });
  const lastRunOf = new Map();
  const revisits = [];
  runs.forEach((run, ri) => {
    const prev = lastRunOf.get(run.clusterId);
    if (prev != null) revisits.push({ fromEnd: runs[prev].end, toStart: run.start, clusterId: run.clusterId });
    lastRunOf.set(run.clusterId, ri);
  });
  let focalIdx = focalEventId ? seq.findIndex((r) => r.eventId === focalEventId) : -1;
  if (focalIdx < 0 && Number.isFinite(focalTsMs)) {
    let best = Infinity;
    seq.forEach((r, i) => {
      const d = Math.abs((r.tsMs ?? 0) - focalTsMs);
      if (d < best) { best = d; focalIdx = i; }
    });
  }
  return { rows: seq, runs, revisits, topClusters, clusterCount: volume.size, focalIdx };
}
